/* @jsx h */
import {
  defineComponent,
  defineAsyncComponent,
  h,
  ref,
  computed,
  watch,
} from "vue";
import type { VNodeChild } from "vue";
import {
  DateRangePicker,
  type DateRange,
  type DateRangeOption,
  useLocalization,
} from "@lemmonhq/uikit";
import { Loading } from "@app/vue/components/UIComponents";
import {
  SegmentSelect,
  type SegmentOption,
  ListMultiSelect,
} from "@lemmonhq/uikit";
import {
  differenceInDays,
  parseISO,
  format,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
  eachQuarterOfInterval,
  eachYearOfInterval,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from "date-fns";
import type { RevenueDatum } from "@lemmonhq/types";

export const PAYMENT_METHODS = [
  {
    key: "cash",
    labelKey: "reports.revenue.cash",
    label: "Cash",
    gradient: ["#f97316", "#b45309"],
    line: "#fb923c",
  },
  {
    key: "card",
    labelKey: "reports.revenue.card",
    label: "Card",
    gradient: ["#60a5fa", "#2563eb"],
    line: "#93c5fd",
  },
  {
    key: "twint",
    labelKey: "reports.revenue.twint",
    label: "TWINT",
    gradient: ["#a855f7", "#6d28d9"],
    line: "#c084fc",
  },
  {
    key: "online",
    labelKey: "reports.revenue.online",
    label: "Online",
    gradient: ["#14b8a6", "#0f766e"],
    line: "#2dd4bf",
  },
  {
    key: "manual",
    labelKey: "reports.revenue.manual",
    label: "Manual",
    gradient: ["#f472b6", "#be185d"],
    line: "#f9a8d4",
  },
] as const;

const RevenueAreaChart = defineAsyncComponent(
  () => import("./RevenueAreaChart"),
);

type SeriesRecord = Record<string, number[]>;
type GroupingDimension = "payment_method" | "service_method";

export type RevenueSummary = {
  totalRevenue: number;
  averageRevenue: number;
  categoriesCount: number;
  groupBy: "day" | "week" | "month" | "quarter" | "year";
  selectedMethodLabel: string;
  selectedServiceMethodLabel: string;
};

const createSeriesRecord = (
  definitions: readonly { key: string }[],
  length: number,
): SeriesRecord => {
  const record: SeriesRecord = {};
  definitions.forEach(({ key }) => {
    record[key] = Array(length).fill(0);
  });
  return record;
};

const SERVICE_METHODS = [
  //  { key: "all", label: "All Service Methods" },
  { key: "dine-in", labelKey: "reports.revenue.dine_in", label: "Dine-in" },
  { key: "takeaway", labelKey: "reports.revenue.takeaway", label: "Takeaway" },
  { key: "delivery", labelKey: "reports.revenue.delivery", label: "Delivery" },
] as const;

const SERVICE_METHOD_SERIES = [
  {
    key: "dine-in",
    labelKey: "reports.revenue.dine_in",
    label: "Dine-in",
    gradient: ["#34d399", "#047857"],
    line: "#6ee7b7",
  },
  {
    key: "takeaway",
    labelKey: "reports.revenue.takeaway",
    label: "Takeaway",
    gradient: ["#f59e0b", "#b45309"],
    line: "#fcd34d",
  },
  {
    key: "delivery",
    labelKey: "reports.revenue.delivery",
    label: "Delivery",
    gradient: ["#818cf8", "#3730a3"],
    line: "#a5b4fc",
  },
] as const;

// Built inside setup() with t() — see serviceSegmentOptions computed below
const SERVICE_SEGMENT_OPTIONS_KEYS = SERVICE_METHODS.map((m) => ({
  value: m.key,
  labelKey: m.labelKey,
}));

export const getDefaultResolutionFromDateRange = (
  startDate: string,
  endDate: string,
): "day" | "week" | "month" | "quarter" | "year" => {
  const days = differenceInDays(new Date(endDate), new Date(startDate));

  if (days <= 31) return "day"; // Up to 1 month: daily view
  if (days <= 90) return "week"; // 1-3 months: weekly view
  if (days <= 730) return "month"; // 3 months - 2 years: monthly view
  if (days <= 1825) return "quarter"; // 2-5 years: quarterly view
  return "year"; // 5+ years: yearly view
};

export const RevenueBars = defineComponent<{
  dateRange: DateRange;
  revenueData: RevenueDatum[];
  loading: boolean;
  showSimplified?: boolean;
  onResolutionChange?: (
    resolution: "day" | "week" | "month" | "quarter" | "year",
  ) => void;
  dateRangeOptions?: DateRangeOption[];
  onRangeChange?: (range: DateRange | undefined) => void;
  onSummaryChange?: (summary: RevenueSummary) => void;
  renderSummary?: () => VNodeChild;
  renderAfterChart?: () => VNodeChild;
}>({
  name: "RevenueBars",
  props: [
    "dateRange",
    "dateRangeOptions",
    "revenueData",
    "loading",
    "showSimplified",
    "onResolutionChange",
    "onRangeChange",
    "onSummaryChange",
    "renderSummary",
    "renderAfterChart",
  ],
  setup(props) {
    const { t } = useLocalization();

    const serviceSegmentOptions = computed<SegmentOption<string>[]>(() =>
      SERVICE_SEGMENT_OPTIONS_KEYS.map((m) => ({
        value: m.value,
        label: t(m.labelKey),
      })),
    );

    const proportionalView = ref(false);
    const manualGroupBy = ref<
      "day" | "week" | "month" | "quarter" | "year" | null
    >(null);
    const groupingDimension = ref<GroupingDimension>("payment_method");

    const autoGroupBy = computed<"day" | "week" | "month" | "quarter" | "year">(
      () => {
        return getDefaultResolutionFromDateRange(
          props.dateRange.startDate,
          props.dateRange.endDate,
        );
      },
    );

    const groupBy = computed<"day" | "week" | "month" | "quarter" | "year">(
      () => manualGroupBy.value || autoGroupBy.value,
    );

    const selectedMethods = ref<string[]>(["all"]);
    const selectedServiceMethods = ref<string[]>(["all"]);
    const paymentMethodSet = new Set<string>(
      PAYMENT_METHODS.map((method) => method.key),
    );
    const serviceMethodKeys = SERVICE_METHOD_SERIES.map((method) => method.key);
    const serviceMethodSet = new Set<string>(serviceMethodKeys);
    const activeMethods = computed<string[]>(() => {
      if (
        selectedMethods.value.includes("all") ||
        selectedMethods.value.length === 0
      ) {
        return PAYMENT_METHODS.map((m) => m.key);
      }
      return selectedMethods.value;
    });
    const activeServiceMethods = computed<string[]>(() => {
      if (
        selectedServiceMethods.value.includes("all") ||
        selectedServiceMethods.value.length === 0
      ) {
        return serviceMethodKeys;
      }
      return selectedServiceMethods.value.filter((key) =>
        serviceMethodSet.has(key),
      );
    });

    // Payment methods drive a ListMultiSelect (id-based). Map each method to its
    // index and back. The "all" sentinel (or empty) maps to no ids selected,
    // which ListMultiSelect renders as "all"; clearing every id restores "all".
    const paymentItems = computed(() =>
      PAYMENT_METHODS.map((method, index) => ({
        id: index,
        name: t(method.labelKey),
      })),
    );
    const selectedPaymentIds = computed<number[]>(() => {
      if (selectedMethods.value.includes("all")) return [];
      return selectedMethods.value
        .map((key) => PAYMENT_METHODS.findIndex((m) => m.key === key))
        .filter((index) => index >= 0);
    });
    const onPaymentSelectionChange = (ids: number[]) => {
      const keys = ids.flatMap((index) => {
        const key = PAYMENT_METHODS[index]?.key;
        return key ? [key] : [];
      });
      selectedMethods.value = keys.length > 0 ? keys : ["all"];
    };
    const seriesData = ref<{
      total: number[];
      payment_method: SeriesRecord;
      service_method: SeriesRecord;
    }>({
      total: [],
      payment_method: createSeriesRecord(PAYMENT_METHODS, 0),
      service_method: createSeriesRecord(SERVICE_METHOD_SERIES, 0),
    });
    const dates = ref<Date[]>([]);
    const currentSeriesDefinitions = computed(() =>
      groupingDimension.value === "payment_method"
        ? PAYMENT_METHODS
        : SERVICE_METHOD_SERIES,
    );
    const categories = computed(() =>
      dates.value.map((date) => {
        switch (groupBy.value) {
          case "day":
            return format(date, "MMM d");
          case "week":
            return format(date, "MMM d");
          case "month":
            return format(date, "MMM yyyy");
          case "quarter":
            return format(date, "'Q'Q yyyy");
          case "year":
            return format(date, "yyyy");
          default:
            return format(date, "MMM d");
        }
      }),
    );

    const totalSeries = computed(() => seriesData.value.total);
    const seriesForActiveDimension = computed(() =>
      groupingDimension.value === "payment_method"
        ? seriesData.value.payment_method
        : seriesData.value.service_method,
    );
    const chartActiveSeriesKeys = computed(() =>
      groupingDimension.value === "payment_method"
        ? activeMethods.value
        : activeServiceMethods.value,
    );
    const displaySeries = computed(() => {
      const keys = chartActiveSeriesKeys.value;
      if (keys.length === 0) {
        return [];
      }
      const length = totalSeries.value.length;
      const result = Array(length).fill(0);
      keys.forEach((key) => {
        const methodData = seriesForActiveDimension.value[key] || [];
        methodData.forEach((value, index) => {
          result[index] += value;
        });
      });
      return result;
    });

    const totalRevenue = computed(() =>
      displaySeries.value.reduce((acc, value) => acc + value, 0),
    );
    const averageRevenue = computed(() => {
      if (!displaySeries.value.length) return 0;
      return totalRevenue.value / displaySeries.value.length;
    });

    const preparedSeries = computed(() =>
      currentSeriesDefinitions.value.map((definition) => ({
        key: definition.key,
        name: t(definition.labelKey),
        gradient: definition.gradient,
        line: definition.line,
        data: seriesForActiveDimension.value[definition.key] || [],
      })),
    );
    const legendItems = computed(() => {
      const activeKeys = new Set(chartActiveSeriesKeys.value);
      const definitions =
        activeKeys.size === 0
          ? currentSeriesDefinitions.value
          : currentSeriesDefinitions.value.filter((definition) =>
              activeKeys.has(definition.key),
            );
      return definitions.map((definition) => ({
        key: definition.key,
        label: t(definition.labelKey),
        gradient: definition.gradient,
      }));
    });

    const isEmpty = computed(() => {
      const keys = chartActiveSeriesKeys.value;
      if (dates.value.length === 0 || keys.length === 0) {
        return true;
      }
      return keys.every((key) => {
        const data = seriesForActiveDimension.value[key] || [];
        return data.every((value) => value === 0);
      });
    });

    const selectedMethodLabel = computed(() => {
      if (
        selectedMethods.value.includes("all") ||
        selectedMethods.value.length === 0
      ) {
        return t("reports.revenue.all_payments");
      }
      if (selectedMethods.value.length === 1) {
        const match = PAYMENT_METHODS.find(
          (m) => m.key === selectedMethods.value[0],
        );
        return match ? t(match.labelKey) : t("reports.revenue.selected");
      }
      return selectedMethods.value
        .map((key) => {
          const m = PAYMENT_METHODS.find((m) => m.key === key);
          return m ? t(m.labelKey) : key;
        })
        .join(" + ");
    });
    const selectedServiceMethodLabel = computed(() => {
      if (
        selectedServiceMethods.value.includes("all") ||
        selectedServiceMethods.value.length === 0
      ) {
        return t("reports.revenue.all_service_methods");
      }
      if (selectedServiceMethods.value.length === 1) {
        const match = SERVICE_METHODS.find(
          (m) => m.key === selectedServiceMethods.value[0],
        );
        return match ? t(match.labelKey) : t("reports.revenue.selected");
      }
      return selectedServiceMethods.value
        .map((key) => {
          const m = SERVICE_METHODS.find((m) => m.key === key);
          return m ? t(m.labelKey) : key;
        })
        .join(" + ");
    });
    const viewingSummary = computed(() => {
      const segments = [selectedMethodLabel.value];
      if (
        !selectedServiceMethods.value.includes("all") &&
        selectedServiceMethods.value.length > 0
      ) {
        segments.push(selectedServiceMethodLabel.value);
      }
      return segments.join(" · ");
    });

    const summary = computed<RevenueSummary>(() => ({
      totalRevenue: totalRevenue.value,
      averageRevenue: averageRevenue.value,
      categoriesCount: categories.value.length,
      groupBy: groupBy.value,
      selectedMethodLabel: selectedMethodLabel.value,
      selectedServiceMethodLabel: selectedServiceMethodLabel.value,
    }));

    watch(
      summary,
      (next) => {
        props.onSummaryChange?.(next);
      },
      { immediate: true },
    );

    const parseRevenueDate = (value: string) => {
      // ensure consistent parse regardless of timezone hints
      return parseISO(value.length > 10 ? value : `${value}T00:00:00`);
    };

    const buildEmptySeriesData = (length: number) => ({
      total: Array(length).fill(0),
      payment_method: createSeriesRecord(PAYMENT_METHODS, length),
      service_method: createSeriesRecord(SERVICE_METHOD_SERIES, length),
    });

    const processData = () => {
      const interval = {
        start: parseISO(props.dateRange.startDate),
        end: parseISO(props.dateRange.endDate),
      };

      let fullDates: Date[];
      switch (groupBy.value) {
        case "year":
          fullDates = eachYearOfInterval(interval);
          break;
        case "quarter":
          fullDates = eachQuarterOfInterval(interval);
          break;
        case "month":
          fullDates = eachMonthOfInterval(interval);
          break;
        case "week":
          fullDates = eachWeekOfInterval(interval, { weekStartsOn: 1 });
          break;
        default:
          fullDates = eachDayOfInterval(interval);
          break;
      }

      const getBucketKeyForInterval = (date: Date) => {
        switch (groupBy.value) {
          case "year":
            return format(startOfYear(date), "yyyy-01-01");
          case "quarter":
            return format(startOfQuarter(date), "yyyy-MM-dd");
          case "week":
            return format(startOfWeek(date, { weekStartsOn: 1 }), "yyyy-MM-dd");
          case "month":
            return format(startOfMonth(date), "yyyy-MM-01");
          default:
            return format(date, "yyyy-MM-dd");
        }
      };

      const bucketIndex = new Map<string, number>();
      fullDates.forEach((date, index) => {
        const key = getBucketKeyForInterval(date);
        bucketIndex.set(key, index);
      });

      const nextSeries = buildEmptySeriesData(fullDates.length);

      // Filter and process the revenue data
      props.revenueData.forEach((row: RevenueDatum) => {
        // Filter by service method if specified
        if (
          !selectedServiceMethods.value.includes("all") &&
          selectedServiceMethods.value.length > 0
        ) {
          const serviceMethod = row.service_method?.toLowerCase();
          if (
            !serviceMethod ||
            !selectedServiceMethods.value.includes(serviceMethod)
          ) {
            return;
          }
        }

        const methodKey = row.payment_method?.toLowerCase();
        if (!methodKey || !paymentMethodSet.has(methodKey)) {
          return;
        }

        const dateKey = (() => {
          const parsed = parseRevenueDate(row.revenue_date);
          return getBucketKeyForInterval(parsed);
        })();
        const index = bucketIndex.get(dateKey);
        if (index === undefined) return;

        const value = Number(row.revenue) || 0;
        if (!Number.isFinite(value)) return;

        nextSeries.payment_method[methodKey][index] += value;

        const serviceKey = row.service_method?.toLowerCase();
        if (serviceKey && serviceMethodSet.has(serviceKey)) {
          nextSeries.service_method[serviceKey][index] += value;
        }

        nextSeries.total[index] += value;
      });

      dates.value = fullDates;
      seriesData.value = nextSeries;
    };

    // Reset manual resolution when date range changes
    watch(
      () => [props.dateRange.startDate, props.dateRange.endDate],
      () => {
        manualGroupBy.value = null; // Reset to auto-select best resolution
      },
    );

    watch(
      () => [
        props.dateRange.startDate,
        props.dateRange.endDate,
        props.revenueData,
        groupBy.value,
        selectedMethods.value,
        selectedServiceMethods.value,
      ],
      () => {
        processData();
        props.onResolutionChange?.(groupBy.value);
      },
      { immediate: true, deep: true },
    );

    const renderChartContent = () => (
      <div class="relative min-h-[360px]">
        {props.loading ? (
          <div class="absolute inset-0 flex items-center justify-center bg-base-100/60 backdrop-blur-sm print:bg-white print:text-neutral-900">
            <Loading />
          </div>
        ) : isEmpty.value ? (
          <div class="absolute inset-0 flex flex-col items-center justify-center text-base-content/60 gap-2">
            <span class="material-symbols-outlined text-5xl opacity-40">
              insights
            </span>
            <p class="text-sm text-center px-4">
              {t("reports.revenue.no_data").replace(
                "{0}",
                viewingSummary.value,
              )}
            </p>
          </div>
        ) : (
          <RevenueAreaChart
            dates={dates.value}
            categories={categories.value}
            series={preparedSeries.value}
            activeSeriesKeys={chartActiveSeriesKeys.value}
            totalSeries={totalSeries.value}
            loading={props.loading}
            groupBy={groupBy.value}
            proportionalView={proportionalView.value}
          />
        )}
      </div>
    );

    return () =>
      props.showSimplified ? (
        renderChartContent()
      ) : (
        <section class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between glossy-bg border-b px-6 py-2 sticky top-0 z-[1]">
            <div class="min-w-[240px] space-y-2 lg:flex-none">
              <label class="flex items-center gap-2 text-sm font-semibold text-base-content/80">
                <span class="material-symbols-outlined text-primary text-3xl">
                  date_range
                </span>
                {t("reports.revenue.date_range")}
              </label>
              <DateRangePicker
                options={props.dateRangeOptions ?? []}
                value={props.dateRange}
                onChange={(value) => {
                  props.onRangeChange?.(value);
                }}
              />
            </div>

            <div class="flex flex-col gap-4 sm:flex-row sm:items-end lg:justify-end">
              <div class="min-w-[200px] space-y-2">
                <label class="flex items-center gap-2 text-sm font-semibold text-base-content/80">
                  <span class="material-symbols-outlined text-primary text-3xl">
                    payment
                  </span>
                  {t("reports.revenue.payment_method")}
                </label>
                <ListMultiSelect
                  items={paymentItems.value}
                  selectedIds={selectedPaymentIds.value}
                  onChange={onPaymentSelectionChange}
                  buttonLabel={t("reports.revenue.payment_method")}
                  searchPlaceholder={t("reports.revenue.search_payment_methods")}
                  compact={true}
                  widthClass="w-full"
                />
              </div>
              <div class="min-w-[200px] space-y-2">
                <label class="flex items-center gap-2 text-sm font-semibold text-base-content/80">
                  <span class="material-symbols-outlined text-primary text-3xl">
                    room_service
                  </span>
                  {t("reports.revenue.service_method")}
                </label>
                <SegmentSelect
                  options={serviceSegmentOptions.value}
                  value={selectedServiceMethods.value}
                  onChange={(value) => {
                    if (!value) {
                      selectedServiceMethods.value = ["all"];
                      return;
                    }

                    if (Array.isArray(value)) {
                      // If "all" was just clicked
                      if (
                        value.includes("all") &&
                        !selectedServiceMethods.value.includes("all")
                      ) {
                        selectedServiceMethods.value = ["all"];
                      }
                      // If a specific method was clicked while "all" was selected
                      else if (
                        value.includes("all") &&
                        selectedServiceMethods.value.includes("all") &&
                        value.length > 1
                      ) {
                        selectedServiceMethods.value = value.filter(
                          (v) => v !== "all",
                        );
                      }
                      // Normal multi-select without "all"
                      else if (!value.includes("all")) {
                        selectedServiceMethods.value =
                          value.length > 0 ? value : ["all"];
                      } else {
                        selectedServiceMethods.value = value;
                      }
                    } else {
                      selectedServiceMethods.value = [value];
                    }
                  }}
                  allowMultiple={true}
                />
              </div>
            </div>
          </div>

          <div class="min-h-0 flex-1 space-y-8 pr-1 print:overflow-visible print:pr-0 px-6 py-2">
            {props.renderSummary?.()}

            <div class="rounded-2xl border  bg-base-100 p-4 shadow-lg print:bg-white print:border-gray-300 print:shadow-none">
              {/* Chart Controls */}
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3 print:hidden">
                <div class="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-base-content/70">
                  {legendItems.value.length > 0 ? (
                    legendItems.value.map((item) => (
                      <div key={item.key} class="flex items-center gap-2">
                        <span
                          class="inline-flex h-3 w-3 rounded-full border border-base-300/50"
                          style={{
                            background: `linear-gradient(135deg, ${item.gradient[0]}, ${item.gradient[1]})`,
                          }}
                        />
                        <span>{item.label ?? item.key}</span>
                      </div>
                    ))
                  ) : (
                    <span class="text-base-content/50 italic">
                      {t("reports.revenue.no_groups")}
                    </span>
                  )}
                </div>
                <div class="flex items-center gap-2">
                  <select
                    class="select select-sm select-bordered bg-base-100 min-w-[120px]"
                    value={groupBy.value}
                    onChange={(e) => {
                      manualGroupBy.value = (e.target as HTMLSelectElement)
                        .value as
                        | "day"
                        | "week"
                        | "month"
                        | "quarter"
                        | "year"
                        | null;
                    }}
                  >
                    <option value="day">
                      {t("reports.revenue.period_day")}
                    </option>
                    <option value="week">
                      {t("reports.revenue.period_week")}
                    </option>
                    <option value="month">
                      {t("reports.revenue.period_month")}
                    </option>
                    <option value="quarter">
                      {t("reports.revenue.period_quarter")}
                    </option>
                    <option value="year">
                      {t("reports.revenue.period_year")}
                    </option>
                  </select>

                  <select
                    class="select select-sm select-bordered bg-base-100 min-w-[150px]"
                    value={groupingDimension.value}
                    onChange={(e) => {
                      groupingDimension.value = (e.target as HTMLSelectElement)
                        .value as GroupingDimension;
                    }}
                  >
                    <option value="payment_method">
                      {t("reports.revenue.payment_method")}
                    </option>
                    <option value="service_method">
                      {t("reports.revenue.service_method")}
                    </option>
                  </select>
                  <button
                    class="btn btn-sm btn-ghost gap-1"
                    onClick={() => {
                      proportionalView.value = !proportionalView.value;
                    }}
                    title={
                      proportionalView.value
                        ? t("reports.revenue.absolute")
                        : t("reports.revenue.proportion")
                    }
                  >
                    <span class="material-symbols-outlined text-base">
                      {proportionalView.value ? "payments" : "percent"}
                    </span>
                    <span class="text-xs">
                      {proportionalView.value
                        ? t("reports.revenue.absolute")
                        : t("reports.revenue.proportion")}
                    </span>
                  </button>
                </div>
              </div>
              {renderChartContent()}
            </div>

            {props.renderAfterChart?.()}

            <div class="hidden text-sm text-base-content/60 print:block">
              {t("reports.revenue.filters_applied")} {viewingSummary.value}
            </div>
          </div>
        </section>
      );
  },
});
